import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { technologies } from '../data/mock';
import { Gear, GearOutline } from './animations/Gear';
import SectionTitle from './SectionTitle';

export default function Technology() {
  return (
    <section id="tech" className="relative py-24 md:py-32 overflow-hidden bg-[#070b12]">
      <div className="absolute inset-0 bg-grid opacity-30" />

      {/* Decorative gear cluster (inspired by reference video) */}
      <div className="pointer-events-none absolute -right-32 top-20 hidden lg:block">
        <div className="relative w-[600px] h-[600px]">
          <div className="absolute top-0 left-0 text-cyan-400/[0.06] spin-slow">
            <Gear size={300} teeth={18} />
          </div>
          <div className="absolute top-32 left-56 text-cyan-300/[0.05] spin-rev">
            <Gear size={220} teeth={14} />
          </div>
          <div className="absolute top-72 left-20 text-cyan-400/[0.05] spin-fast">
            <Gear size={180} teeth={20} />
          </div>
          <div className="absolute top-44 left-96 text-cyan-300/[0.07] spin-slow">
            <GearOutline size={160} teeth={36} />
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle code="SECTION / 03" title="SİSTEM TEKNOLOJİLERİ" subtitle="UTKU sınıfını çağının ötesine taşıyan alt sistemler." />

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-cyan-500/15 border border-cyan-500/15">
          {technologies.map((t, i) => (
            <TechCard key={t.code} t={t} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TechCard({ t, i }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
      className="group relative bg-[#0a0f17] p-7 hover:bg-[#0d141f] transition-colors min-h-[280px] flex flex-col"
    >
      {/* Code badge */}
      <div className="flex items-start justify-between mb-6">
        <span className="font-mono text-[10px] tracking-[0.25em] text-cyan-400/70">{t.code}</span>
        <ArrowUpRight className="w-5 h-5 text-cyan-400/30 group-hover:text-cyan-300 group-hover:rotate-45 transition-all" />
      </div>

      <div className="flex-1">
        <h3 className="font-display text-2xl font-bold text-white">{t.title}</h3>
        <p className="text-[11px] font-mono tracking-[0.2em] text-cyan-300/70 mt-1">{t.subtitle}</p>
        <p className="text-sm text-slate-400 mt-4 leading-relaxed">{t.description}</p>
      </div>

      <div className="mt-6 pt-5 border-t border-dashed border-cyan-500/15 flex items-baseline justify-between">
        <span className="font-display text-3xl font-bold text-cyan-300">{t.metric}</span>
        <span className="text-[9px] font-mono tracking-[0.25em] text-slate-500">{t.metricLabel}</span>
      </div>

      {/* Hover bar */}
      <div className="absolute bottom-0 left-0 h-px bg-cyan-400 w-0 group-hover:w-full transition-all duration-700" />
    </motion.div>
  );
}
